/**
 * Fetch All Projects With Pairs
 *
 * Consolidated API fetcher that loads all projects and their pairs.
 * Extracted from the Redux thunk for reuse with React Query.
 *
 * Note: This still has N+1 API calls (1 for projects + N for pairs per project)
 * because the backend doesn't provide a single endpoint for all data.
 * However, with caching this is only called on cache miss/invalidation.
 */

import dayjs from 'dayjs';

import { ApiPairs, ApiProjects } from 'api';
import { IProject, IProjectShort } from 'types/project';
import { EExchange } from 'web3';

/**
 * Result of fetching all projects with pairs
 */
export interface FetchProjectsResult {
  projects: IProject[];
  archivedProjects: IProject[];
}

/**
 * Fill a list of projects with their pairs
 * Fetches pairs for each project in parallel
 */
const fillProjectsWithPairs = async (projects: IProjectShort[]): Promise<IProject[]> => {
  // Sort projects by created_at date
  const sortedProjects = [...projects].sort((a, b) => {
    const dateA = dayjs(a.created_at).valueOf();
    const dateB = dayjs(b.created_at).valueOf();
    return dateA - dateB;
  });

  // Initialize projects with empty pair arrays
  const projectsWithEmptyPairs: IProject[] = sortedProjects.map(project => ({
    ...project,
    dexPairs: [],
    cexPairs: [],
    solPairs: [],
  }));

  // Fetch pairs for all projects in parallel
  const projectsWithPairs = await Promise.all(
    projectsWithEmptyPairs.map(async project => {
      try {
        const { isSuccess, data } = await ApiPairs.getPairsByProjectId({ id: project.id });

        if (!isSuccess || !data?.items) {
          return project;
        }

        // Categorize pairs by type
        const dexPairs = data.items
          .filter(item => item.type === EExchange.dex)
          .map(item => item.dex_pair!)
          .filter(Boolean);

        const cexPairs = data.items
          .filter(item => item.type === EExchange.cex)
          .map(item => item.cex_pair!)
          .filter(Boolean);

        const solPairs = data.items
          .filter(item => item.type === EExchange.sol)
          .map(item => item.sol_pair!)
          .filter(Boolean);

        return {
          ...project,
          dexPairs,
          cexPairs,
          solPairs,
        };
      } catch (error) {
        console.error(
          `[fetchAllProjectsWithPairs] Failed to fetch pairs for project ${project.id}`,
          error,
        );
        return project;
      }
    }),
  );

  return projectsWithPairs;
};

/**
 * Fetch all projects (active and archived) with their pairs
 *
 * @returns Object with projects and archivedProjects arrays
 * @throws Error if API call fails
 */
export const fetchAllProjectsWithPairs = async (): Promise<FetchProjectsResult> => {
  const { data, isSuccess, errorMessage } = await ApiProjects.getAllProjects({});

  if (!isSuccess || !data) {
    throw new Error(errorMessage || 'Failed to fetch projects');
  }

  // Fetch pairs for both active and archived projects in parallel
  const [projects, archivedProjects] = await Promise.all([
    fillProjectsWithPairs(data.items || []),
    fillProjectsWithPairs(data.archived_items || []),
  ]);

  return {
    projects,
    archivedProjects,
  };
};
